import { Icon, IconButton, Stack, TextField } from "@mui/material";
import React, { useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { talkActions } from "store/talk";
import { popupActions } from "store/react-lib/generic-ui/popup";
import { addDoc, collection } from "firebase/firestore";
import { db, functions } from "firebaseApp";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import { getTalkId } from "utils/talk";
import useIsMobile from "hooks/react-lib/generic-ui/useIsMobile";
import { useTheme } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import ToolSelectorMenu from "components/bot/ToolSelectorMenu";
import { httpsCallable } from "firebase/functions";
import RecordButton from "components/react-lib/generic-ui/RecordButton";
import { convertBlobURLToBase64 } from "utils/audio";

export default function TalkInput({
  onSendMessage = () => {},
  onFocus = () => {},
}) {
  const theme = useTheme();
  const params = useParams();
  const dispatch = useDispatch();
  const auth = useAuth();

  const uid = auth.currentUser.uid;
  const friendId = params.friendId;

  const talkId = getTalkId(uid, friendId);
  const [shiftOn, setShiftOn] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [processingTool, setProcessingTool] = useState(false);

  const isMobile = useIsMobile();

  const value = useSelector((state) => state.talk.input[friendId]) || "";
  const audioState = useSelector((state) => state.talk.audio);

  const handleUpdateInput = (e) => {
    dispatch(
      talkActions.updateInput({
        friendId,
        newInput: e.target.value,
      })
    );
  };

  const disabled = () => {
    if (processingTool) return true;
    if (shiftOn) return true;
    if (!talkId) return true;
    if (audioState.audioUrl) return friendId !== audioState.friendId;
    if (value.trim().length === 0) return true;
    return false;
  };

  const handleSendMessage = async () => {
    let audioBase64 = null;
    if (audioState.audioUrl) {
      audioBase64 = await convertBlobURLToBase64(audioState.audioUrl);
    }
    addDoc(collection(db, "talks", talkId, "messages"), {
      content: value,
      sender: uid,
      receiver: friendId,
      createdAt: new Date().getTime(),
      talkId,
      audioBase64,
      audioUrl: audioState.audioUrl,
    })
      .then(() => {})
      .catch((error) => {
        console.log(error);
        dispatch(popupActions.showError("Internal error"));
      });
    onSendMessage();
    dispatch(talkActions.clearInput({ friendId }));
    dispatch(talkActions.clearAudio());
  };

  const handleKeyDown = (event) => {
    if (disabled()) return;
    setShiftOn(Boolean(event.shiftKey));
    if (event.key === "Enter" && !event.shiftKey && !isComposing) {
      if (!isMobile) {
        event.preventDefault();
        handleSendMessage();
      }
    }
  };

  const handleKeyUp = (event) => {
    setShiftOn(Boolean(event.shiftKey));
  };

  const applyToolToPrompt = async (tool) => {
    try {
      setProcessingTool(true);
      const res = await httpsCallable(
        functions,
        "applyToolToContent"
      )({
        content: value,
        toolId: tool.id,
      });
      const resData = res.data;
      if (!resData.success) {
        dispatch(popupActions.showError(resData.error));
      } else {
        dispatch(
          talkActions.updateInput({
            friendId,
            newInput: resData.answer,
          })
        );
      }
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Something went wrong."));
    } finally {
      setProcessingTool(false);
    }
  };

  const handleStartAudioRecording = () => {
    dispatch(
      talkActions.startRecording({
        friendId,
      })
    );
  };

  const handleClearRecording = () => {
    dispatch(
      talkActions.clearAudio({
        friendId,
      })
    );
  };

  const renderPlaceholder = () => {
    if (audioState.recording || audioState.audioUrl) {
      return "Clear recorded audio to type";
    }
    return "Send a message";
  };

  const renderValue = () => {
    if (audioState.recording || audioState.audioUrl) {
      return "Clear recorded audio to type";
    }
    return value;
  };

  return (
    <Stack spacing={1} width="100%" maxWidth="748px">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row">
          <RecordButton
            startRecordingCallback={handleStartAudioRecording}
            clearRecordingCallback={handleClearRecording}
            audioURL={audioState.audioUrl}
            setAudioURL={(audioUrl) =>
              dispatch(talkActions.setAudioUrl({ audioUrl }))
            }
          />
          <ToolSelectorMenu
            onToolSelected={applyToolToPrompt}
            processing={processingTool}
          />
        </Stack>
        <Stack direction="row">
          <IconButton
            disabled={!value}
            onClick={() => dispatch(talkActions.clearInput({ friendId }))}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <TextField
          color="secondary"
          maxRows={6}
          placeholder={renderPlaceholder()}
          fullWidth
          disabled={
            processingTool || audioState.audioUrl || audioState.recording
          }
          multiline
          value={renderValue()}
          onFocus={onFocus}
          onChange={handleUpdateInput}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          autoComplete="off"
          InputProps={{
            style: {
              backgroundColor: theme.palette.inputBackground.main,
              color: theme.palette.text.main,
            },
          }}
        />
        <IconButton
          color="secondary"
          onClick={handleSendMessage}
          disabled={disabled()}
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}
